import React from 'react';
import { isPlainObject } from './workflow-schema-utils';

export type SourceType = 'literal' | 'workflow-input' | 'step-output' | 'mixed';

const INPUT_REF_REGEX = /\{\{\s*inputs\.([^}]+)\}\}/g;
const STEP_OUTPUT_REF_REGEX = /\{\{\s*steps\.([^.}]+)\.outputs\.([^}]+)\}\}/g;

const detectSource = (value: unknown): SourceType => {
  if (typeof value !== 'string') return 'literal';
  const hasInput = INPUT_REF_REGEX.test(value);
  INPUT_REF_REGEX.lastIndex = 0;
  const hasStep = STEP_OUTPUT_REF_REGEX.test(value);
  STEP_OUTPUT_REF_REGEX.lastIndex = 0;
  if (hasInput && hasStep) return 'mixed';
  if (hasInput) return 'workflow-input';
  if (hasStep) return 'step-output';
  return 'literal';
};

type FlatItem = { path: string; value: unknown; source: SourceType };

const flattenInputs = (value: unknown, basePath = ''): FlatItem[] => {
  if (Array.isArray(value)) {
    const items: FlatItem[] = [];
    value.forEach((v, i) => {
      const p = `${basePath}[${i}]`;
      if (isPlainObject(v) || Array.isArray(v)) {
        items.push(...flattenInputs(v, p));
      } else {
        items.push({ path: p, value: v, source: detectSource(v) });
      }
    });
    return items;
  }
  if (isPlainObject(value)) {
    const items: FlatItem[] = [];
    Object.entries(value).forEach(([k, v]) => {
      const p = basePath ? `${basePath}.${k}` : k;
      if (isPlainObject(v) || Array.isArray(v)) {
        items.push(...flattenInputs(v, p));
      } else {
        items.push({ path: p, value: v, source: detectSource(v) });
      }
    });
    return items;
  }
  return basePath
    ? [{ path: basePath, value, source: detectSource(value) }]
    : [];
};

const SourceBadge: React.FC<{ source: SourceType }> = ({ source }) => {
  const styles: Record<SourceType, string> = {
    literal: 'bg-slate-100 text-slate-700 border-slate-200',
    'workflow-input': 'bg-blue-100 text-blue-700 border-blue-200',
    'step-output': 'bg-violet-100 text-violet-700 border-violet-200',
    mixed: 'bg-amber-100 text-amber-800 border-amber-200',
  };
  const labels: Record<SourceType, string> = {
    literal: 'Valor literal',
    'workflow-input': 'Entrada del flujo',
    'step-output': 'Salida de un paso',
    mixed: 'Mixto',
  };
  return (
    <span className={`px-2 py-0.5 rounded border ${styles[source]}`}>
      {labels[source]}
    </span>
  );
};

const formatPreview = (v: unknown): string => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
};

export const InputsFlatList: React.FC<{ value: unknown }> = ({ value }) => {
  const items = React.useMemo(() => flattenInputs(value), [value]);
  if (!items.length) {
    return <div className="text-slate-500">Sin entradas configuradas.</div>;
  }

  const borderAccent: Record<SourceType, string> = {
    literal: 'border-l-slate-300',
    'workflow-input': 'border-l-blue-400',
    'step-output': 'border-l-violet-400',
    mixed: 'border-l-amber-400',
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2">
      {items.map((item, idx) => (
        <div
          key={`${item.path}-${idx}`}
          className={`border rounded bg-white p-3 flex flex-col gap-1 border-slate-200 border-l-4 ${borderAccent[item.source]}`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="font-medium text-slate-800 break-words">
              {item.path}
            </div>
            <SourceBadge source={item.source} />
          </div>
          <div className="text-slate-700 break-words">
            {formatPreview(item.value)}
          </div>
        </div>
      ))}
    </div>
  );
};

export default InputsFlatList;
